/*
 * Vajihi Labs - Email Signature Builder
 * Persistence layer, v3: the nested signature data model, local
 * (browser-only) save/load with migration from the pre-Sept-14 flat model,
 * and the team-sharing "share code" export/import format.
 *
 * Local persistence uses this task pane's own localStorage — it lives only
 * on this device, in this Outlook client's task pane. There is no cloud
 * sync; closing the pane on a different computer starts from a blank
 * design there.
 */

import { defaultModel, defaultSocials, LEGACY_THEME_IDS, LEGACY_THEME_MIGRATION } from "./themes";
import type { BrandingFields, LegacyThemeId, LogoFields, SignatureModel, SocialEntry, SocialType, ThemeId } from "./themes";
import { isTokenValue, isValidHexColor, sanitizeUrl } from "./validation";

/** A stored personal-field value that still looks like a {{Token}}
 *  placeholder from the pre-Sept-14 template-token system is not a real
 *  value — v3 has no token concept, so treat it as unset rather than
 *  displaying literal "{{Title}}"/"{{Phone}}" text to the person. */
function cleanPersonValue(value: unknown, fallback: string): string {
  if (typeof value !== "string") return fallback;
  return isTokenValue(value) ? "" : value;
}

const STORAGE_KEY = "vajihi-signature-builder:model:v3";
/** The pre-Sept-14 flat model's storage key — read once for migration,
 *  never written to again, never deleted (so nothing is silently lost if
 *  migration has a bug and the person needs to recover the old data). */
const LEGACY_STORAGE_KEY = "vajihi-signature-builder:model:v2";
const SHARE_CODE_VERSION = 3;

const VALID_THEME_IDS: ThemeId[] = ["basic", "premium-executive-v2", "premium-studio-v2", "premium-prestige-v2", "premium-connect-v2", "premium-essential-v2"];
const VALID_SOCIAL_TYPES: SocialType[] = ["linkedin", "instagram", "facebook", "x", "website"];
const LEGACY_DEFAULT_BRAND_COLOR = "#3B5BFD";

function cloneModel(model: SignatureModel): SignatureModel {
  return {
    ...model,
    person: { ...model.person },
    company: { ...model.company },
    logos: { ...model.logos },
    branding: { ...model.branding },
    socials: model.socials.map((s) => ({ ...s })),
    arabic: { ...model.arabic },
  };
}

function mergeSocials(base: SocialEntry[], raw: unknown[]): SocialEntry[] {
  const byId = new Map(base.map((s) => [s.id, { ...s }]));
  raw.forEach((entry) => {
    if (!entry || typeof entry !== "object") return;
    const record = entry as Record<string, unknown>;
    const id = record.id as SocialType;
    if (!VALID_SOCIAL_TYPES.includes(id)) return;
    const existing = byId.get(id) ?? { id, enabled: false, url: "" };
    byId.set(id, {
      id,
      enabled: typeof record.enabled === "boolean" ? record.enabled : existing.enabled,
      // Keep the raw text for editing even if it fails sanitizeUrl — the
      // renderer re-sanitizes at export/apply time regardless, so an unsafe
      // scheme typed here is never actually output, just visible for the
      // person to notice and fix.
      url: typeof record.url === "string" ? record.url : existing.url,
    });
  });
  return VALID_SOCIAL_TYPES.map((id) => byId.get(id) ?? { id, enabled: false, url: "" });
}

function mergeLogos(base: LogoFields, raw: unknown): LogoFields {
  if (!raw || typeof raw !== "object") return { ...base };
  const record = raw as Record<string, unknown>;
  return {
    lightSurfaceUrl: typeof record.lightSurfaceUrl === "string" ? record.lightSurfaceUrl : base.lightSurfaceUrl,
    darkSurfaceUrl: typeof record.darkSurfaceUrl === "string" ? record.darkSurfaceUrl : base.darkSurfaceUrl,
    alt: typeof record.alt === "string" ? record.alt : base.alt,
  };
}

function mergeBranding(base: BrandingFields, raw: unknown): BrandingFields {
  if (!raw || typeof raw !== "object") return { ...base };
  const record = raw as Record<string, unknown>;
  const accentOverride =
    typeof record.accentOverride === "string" && isValidHexColor(record.accentOverride) ? record.accentOverride : base.accentOverride;
  return {
    useThemePalette: typeof record.useThemePalette === "boolean" ? record.useThemePalette : base.useThemePalette,
    accentOverride,
    tagline: typeof record.tagline === "string" ? record.tagline : base.tagline,
    footerStatement: typeof record.footerStatement === "string" ? record.footerStatement : base.footerStatement,
    sideStatement: typeof record.sideStatement === "string" ? record.sideStatement : base.sideStatement,
  };
}

/** Merges a v3-shaped (or share-code) JSON object into a base model. Used
 *  for both localStorage load and share-code import — `fields` controls
 *  which top-level sections are read, so a share-code import can skip
 *  `person` entirely and a localStorage load can include it. */
function mergeIntoModel(base: SignatureModel, raw: unknown, fields: { person: boolean } = { person: true }): SignatureModel {
  const model = cloneModel(base);
  if (!raw || typeof raw !== "object") return model;
  const record = raw as Record<string, unknown>;

  if (typeof record.themeId === "string" && VALID_THEME_IDS.includes(record.themeId as ThemeId)) {
    model.themeId = record.themeId as ThemeId;
  }
  if (fields.person && record.person && typeof record.person === "object") {
    const p = record.person as Record<string, unknown>;
    model.person = {
      name: cleanPersonValue(p.name, model.person.name),
      title: cleanPersonValue(p.title, model.person.title),
      phone: cleanPersonValue(p.phone, model.person.phone),
      email: cleanPersonValue(p.email, model.person.email),
    };
  }
  if (record.company && typeof record.company === "object") {
    const c = record.company as Record<string, unknown>;
    model.company = {
      name: typeof c.name === "string" ? c.name : model.company.name,
      website: typeof c.website === "string" ? c.website : model.company.website,
      websiteLabel: typeof c.websiteLabel === "string" ? c.websiteLabel : model.company.websiteLabel,
    };
  }
  model.logos = mergeLogos(model.logos, record.logos);
  model.branding = mergeBranding(model.branding, record.branding);
  if (Array.isArray(record.socials)) {
    model.socials = mergeSocials(model.socials, record.socials);
  }
  if (typeof record.disclaimer === "string") model.disclaimer = record.disclaimer;
  if (record.arabic && typeof record.arabic === "object") {
    const a = record.arabic as Record<string, unknown>;
    model.arabic = {
      name: typeof a.name === "string" ? a.name : model.arabic.name,
      title: typeof a.title === "string" ? a.title : model.arabic.title,
      companyName: typeof a.companyName === "string" ? a.companyName : model.arabic.companyName,
    };
  }
  return model;
}

/** True if a legacy flat model's stored themeId is one of the pre-Sept-14
 *  ids (executive/minimal/corporate/modern/compact/bilingual). */
function isLegacyThemeId(value: unknown): value is LegacyThemeId {
  return typeof value === "string" && (LEGACY_THEME_IDS as string[]).includes(value);
}

/**
 * Migrates the pre-Sept-14 flat model (themeId, name, companyName, title,
 * phone, website, disclaimer, brandColor, logoUrl, social[], arabic) into
 * the v3 nested shape, per integration/migration.json's rules: preserve
 * every saved field, map the theme id, never discard Arabic/bilingual
 * content even though Bilingual has no new-design equivalent.
 */
function migrateLegacyModel(raw: Record<string, unknown>): SignatureModel {
  const base = defaultModel();
  const model = cloneModel(base);

  const legacyThemeId = raw.themeId;
  if (isLegacyThemeId(legacyThemeId)) {
    const mapped = LEGACY_THEME_MIGRATION[legacyThemeId];
    // `bilingual` maps to null (no equivalent design) — default to
    // premium-executive-v2 and let the person pick a real theme in
    // Templates; the Arabic content itself is preserved below either way.
    model.themeId = mapped ?? "premium-executive-v2";
  }

  model.person = {
    name: cleanPersonValue(raw.name, ""),
    title: cleanPersonValue(raw.title, ""),
    phone: cleanPersonValue(raw.phone, ""),
    email: cleanPersonValue(raw.email, ""),
  };
  model.company = {
    name: typeof raw.companyName === "string" ? raw.companyName : "",
    website: typeof raw.website === "string" ? raw.website : "",
    websiteLabel: "",
  };
  const legacyLogoUrl = typeof raw.logoUrl === "string" ? sanitizeUrl(raw.logoUrl) : "";
  model.logos = { lightSurfaceUrl: legacyLogoUrl, darkSurfaceUrl: "", alt: model.company.name || "Company logo" };

  const legacyColor = typeof raw.brandColor === "string" && isValidHexColor(raw.brandColor) ? raw.brandColor.toUpperCase() : null;
  // Only carry the color forward as an override if the person actually
  // changed it from the old app's own default — otherwise let each new
  // theme adopt its own distinct palette (Studio's emerald, Prestige's
  // bronze, etc.) rather than tinting every migrated theme the old blue.
  const customized = legacyColor !== null && legacyColor !== LEGACY_DEFAULT_BRAND_COLOR;
  model.branding = {
    useThemePalette: !customized,
    accentOverride: customized ? legacyColor : null,
    tagline: "",
    footerStatement: "",
    sideStatement: "",
  };

  if (Array.isArray(raw.social)) {
    model.socials = mergeSocials(defaultSocials(), raw.social as unknown[]);
  }
  model.disclaimer = typeof raw.disclaimer === "string" ? raw.disclaimer : "";
  if (raw.arabic && typeof raw.arabic === "object") {
    const a = raw.arabic as Record<string, unknown>;
    model.arabic = {
      name: typeof a.name === "string" ? a.name : "",
      title: typeof a.title === "string" ? a.title : "",
      companyName: typeof a.companyName === "string" ? a.companyName : "",
    };
  }
  return model;
}

export function loadModel(): SignatureModel {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (raw) {
      return mergeIntoModel(defaultModel(), JSON.parse(raw));
    }
  } catch {
    // fall through to legacy/default below
  }

  try {
    const legacyRaw = window.localStorage.getItem(LEGACY_STORAGE_KEY);
    if (legacyRaw) {
      const migrated = migrateLegacyModel(JSON.parse(legacyRaw));
      saveModel(migrated); // persist the migration so it only runs once
      return migrated;
    }
  } catch {
    // fall through to default
  }

  return defaultModel();
}

export function saveModel(model: SignatureModel): void {
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(model));
  } catch {
    // Storage can be unavailable (private browsing, quota). Persistence is
    // best-effort — it never blocks using the app within the current session.
  }
}

export interface ShareCodeResult {
  model: SignatureModel;
  errors: string[];
}

/** Excludes every personal field (name/title/phone/email) deliberately —
 *  Customize is where each person fills in their own info, so a shared
 *  design only ever carries the "look": theme, company, logo, branding,
 *  socials and disclaimer. */
export function encodeShareCode(model: SignatureModel): string {
  const payload = {
    v: SHARE_CODE_VERSION,
    themeId: model.themeId,
    company: model.company,
    logos: model.logos,
    branding: model.branding,
    socials: model.socials,
    disclaimer: model.disclaimer,
  };
  return JSON.stringify(payload, null, 2);
}

export function decodeShareCode(raw: string): ShareCodeResult {
  const base = defaultModel();
  let data: unknown;
  try {
    data = JSON.parse(raw);
  } catch {
    return { model: base, errors: ["That doesn't look like valid signature code — check you copied the whole thing."] };
  }
  if (!data || typeof data !== "object") {
    return { model: base, errors: ["That doesn't look like valid signature code."] };
  }
  const record = data as Record<string, unknown>;
  if (record.v !== SHARE_CODE_VERSION) {
    const message =
      typeof record.v === "number" && record.v < SHARE_CODE_VERSION
        ? "This code was made with an older version of the add-in (before the premium theme redesign). Ask your designer to re-share it from the updated Templates/Customize tabs."
        : "This code was made with a different version of the add-in. Ask your designer to re-share it.";
    return { model: base, errors: [message] };
  }
  const errors: string[] = [];
  if (typeof record.themeId !== "string" || !VALID_THEME_IDS.includes(record.themeId as ThemeId)) {
    errors.push("Signature code is missing a valid theme.");
  }
  // Never let an imported code touch personal fields, even if someone
  // hand-edited one to include them.
  const model = mergeIntoModel(base, record, { person: false });
  return { model, errors };
}
